// src/components/ModalsContainer/DeviceUpdateBanner.js
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useUIStore, useDevicesStore } from "../../stores";
import config from "../../config";
import logger from "../../utils/logger";
import "./DeviceUpdateBanner.css";

/**
 * Non-blocking status banner for firmware/WiFi update progress.
 *
 * Replaces the modal dialog with a small fixed-position card (bottom-right)
 * that shows update progress without interrupting the user.
 *
 * Dual-gate approach:
 *   Both conditions must be met before auto-refreshing:
 *   1. DEVICE_NOTIFICATION_REPORTED with connectivityStatus "ONLINE" (phase → 'ready')
 *   2. Minimum delay elapsed since RESOLVED (MIN_DELAY_AFTER_RESOLVED_MS)
 *
 * Why: Yonomi's GraphQL API takes 45-75s after RESOLVED to serve updated WiFi
 * SSID data. The deviceStateUpdate events arrive much earlier (~11s) but only
 * carry lock/door state — not WiFi data. The ONLINE connectivity notification
 * is the reliable signal that the device has reconnected after the WiFi change
 * and Yonomi has propagated the new state.
 *
 * Phases:
 *   1. 'waiting'    – RESOLVED received, no deviceStateUpdate yet
 *   2. 'ready'      – deviceStateUpdate received; if min delay not reached, shows
 *                     "propagating" message; once delay reached → auto-syncs
 *   3. 'refreshing' – backend sync + store refresh in progress
 *   4. done         – banner auto-closes on success
 *
 * Fallback: after FALLBACK_TIMEOUT_MS, shows manual "Refresh Now" button.
 *
 * @param {Object} props
 * @param {Function} props.authenticatedFetch - Authenticated fetch wrapper
 * @param {string} props.installationId - Current installation ID
 */

/**
 * Minimum time (ms) to wait after RESOLVED before syncing.
 * Small buffer to ensure Yonomi's GraphQL API has the updated data
 * after the device reconnects. The ONLINE connectivity event is the
 * primary gate; this timer is a secondary safety buffer.
 */
const MIN_DELAY_AFTER_RESOLVED_MS = 10_000; // 10 seconds

/** After this long without resolution, show a manual "Refresh Now" button */
const FALLBACK_TIMEOUT_MS = 120_000; // 2 minutes

const BANNER_CONFIG = {
  firmware: {
    title: "Firmware Update",
    waitingMessage: "Waiting for device to report new state…",
    propagatingMessage:
      "Device acknowledged. Waiting for data to propagate. If you don't see a change within 5 minutes, please click the 'Refresh All Data' button.",
    refreshingMessage: "Refreshing device data…",
    completeMessage: "Firmware data refreshed!",
    emoji: "🔄",
  },
  wifi: {
    title: "WiFi Settings",
    waitingMessage: "Waiting for device to report new state…",
    propagatingMessage:
      "Device acknowledged. Waiting for data to propagate. If you don't see a change within 5 minutes, please click the 'Refresh All Data' button.",
    refreshingMessage: "Refreshing device data…",
    completeMessage: "WiFi data refreshed!",
    emoji: "📶",
  },
};

const DeviceUpdateBanner = ({ authenticatedFetch, installationId }) => {
  const deviceUpdateModal = useUIStore((state) => state.deviceUpdateModal);
  const closeDeviceUpdateModal = useUIStore(
    (state) => state.closeDeviceUpdateModal,
  );
  const setDeviceUpdatePhase = useUIStore(
    (state) => state.setDeviceUpdatePhase,
  );
  const refreshDevices = useDevicesStore((state) => state.refreshDevices);

  const [showFallback, setShowFallback] = useState(false);
  const [minDelayReached, setMinDelayReached] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const fallbackTimerRef = useRef(null);
  const minDelayTimerRef = useRef(null);
  const hasTriggeredRefresh = useRef(false);

  const { isOpen, type, deviceId, phase } = deviceUpdateModal;
  const bannerConfig = BANNER_CONFIG[type] || BANNER_CONFIG.wifi;

  // --- Timers: start when banner opens in 'waiting' phase ---
  useEffect(() => {
    if (isOpen && phase === "waiting") {
      setShowFallback(false);
      setMinDelayReached(false);
      setIsClosing(false);
      hasTriggeredRefresh.current = false;

      // Minimum delay timer — Yonomi needs ~45-75s to propagate WiFi/FW data
      minDelayTimerRef.current = setTimeout(() => {
        logger.log(
          `[DeviceUpdateBanner] ⏱ Minimum propagation delay reached (${MIN_DELAY_AFTER_RESOLVED_MS / 1000}s since RESOLVED)`,
        );
        setMinDelayReached(true);
      }, MIN_DELAY_AFTER_RESOLVED_MS);

      // Fallback timer — show manual button if nothing works
      fallbackTimerRef.current = setTimeout(() => {
        logger.log(
          `[DeviceUpdateBanner] ⏰ Fallback timeout reached (${FALLBACK_TIMEOUT_MS / 1000}s) — showing manual refresh button`,
        );
        setShowFallback(true);
      }, FALLBACK_TIMEOUT_MS);
    }
    return () => {
      if (minDelayTimerRef.current) {
        clearTimeout(minDelayTimerRef.current);
        minDelayTimerRef.current = null;
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [isOpen, phase]);

  // --- Auto-refresh when BOTH gates are satisfied ---
  // Gate 1: deviceStateUpdate received (phase === 'ready')
  // Gate 2: minimum propagation delay elapsed (minDelayReached === true)
  useEffect(() => {
    if (phase === "ready" && minDelayReached && !hasTriggeredRefresh.current) {
      hasTriggeredRefresh.current = true;
      logger.log(
        `[DeviceUpdateBanner] ✅ Both gates met (deviceStateUpdate + ${MIN_DELAY_AFTER_RESOLVED_MS / 1000}s delay) — triggering sync`,
      );
      triggerRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, minDelayReached]);

  /** Animate close → then actually close store state */
  const closeBanner = useCallback(() => {
    setIsClosing(true);
    // Allow CSS exit animation to complete
    setTimeout(() => {
      closeDeviceUpdateModal();
      setIsClosing(false);
    }, 300);
  }, [closeDeviceUpdateModal]);

  /** POST /refresh-data → refreshDevices → close banner */
  const triggerRefresh = useCallback(() => {
    if (!authenticatedFetch || !installationId) return;

    const label = bannerConfig.emoji;
    setDeviceUpdatePhase("refreshing");

    logger.log(
      `[DeviceUpdateBanner] ${label} Triggering backend sync + store refresh for device ${deviceId}`,
    );

    authenticatedFetch(`${config.apiEndpoint}/refresh-data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
      },
    })
      .then(() => {
        logger.log(
          `[DeviceUpdateBanner] ${label} Backend sync complete, refreshing store for device ${deviceId}`,
        );
        return refreshDevices(installationId, authenticatedFetch);
      })
      .then(() => {
        logger.log(
          `[DeviceUpdateBanner] ${label} Full refresh complete for device ${deviceId}`,
        );
        setDeviceUpdatePhase("complete");
        // Auto-close after brief "complete" display
        setTimeout(() => {
          closeBanner();
        }, 2000);
      })
      .catch((err) => {
        logger.error(
          `[DeviceUpdateBanner] ${label} Full refresh failed:`,
          err.message,
        );
        closeBanner();
      });
  }, [
    authenticatedFetch,
    installationId,
    deviceId,
    bannerConfig.emoji,
    refreshDevices,
    closeBanner,
    setDeviceUpdatePhase,
  ]);

  if (!isOpen) return null;

  // --- Determine banner content based on phase ---
  const isWaiting = phase === "waiting";
  const isReady = phase === "ready";
  const isRefreshing = phase === "refreshing";
  const isComplete = phase === "complete";

  let statusMessage;
  if (isWaiting && !showFallback) {
    statusMessage = bannerConfig.waitingMessage;
  } else if (isWaiting && showFallback) {
    statusMessage = bannerConfig.waitingMessage;
  } else if (isReady && !minDelayReached) {
    statusMessage = bannerConfig.propagatingMessage;
  } else if (isRefreshing) {
    statusMessage = bannerConfig.refreshingMessage;
  } else if (isComplete) {
    statusMessage = bannerConfig.completeMessage;
  } else {
    statusMessage = bannerConfig.refreshingMessage;
  }

  const showSpinner =
    isWaiting || (isReady && !minDelayReached) || isRefreshing;
  const canClickRefresh = isWaiting && showFallback;

  return (
    <div
      className={`device-update-banner ${isClosing ? "banner-exit" : "banner-enter"} ${isComplete ? "banner-complete" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="banner-header">
        <span className="banner-emoji">{bannerConfig.emoji}</span>
        <span className="banner-title">{bannerConfig.title}</span>
        <button
          className="banner-dismiss"
          onClick={closeBanner}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>

      <div className="banner-body">
        {showSpinner && <span className="banner-spinner" aria-hidden="true" />}
        {isComplete && (
          <span className="banner-check" aria-hidden="true">
            ✓
          </span>
        )}
        <span className="banner-message">{statusMessage}</span>
      </div>

      {canClickRefresh && (
        <div className="banner-actions">
          <button className="banner-refresh-btn" onClick={triggerRefresh}>
            Refresh Now
          </button>
        </div>
      )}
    </div>
  );
};

export default DeviceUpdateBanner;
